import React from 'react';
import { useJobSetupStore } from '@presentation/stores/JobSetupStore';
import { useFileExplorerStore } from '@presentation/stores/FileExplorerStore';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svgIcons from '@progress/kendo-svg-icons';

import { JobPipelineTypeSchema } from '@domain/schemas/BI';

export const RawFileFormBlock: React.FC = () => {
    const { rawFiles, pipelineType, archiveFiles } = useJobSetupStore((state) => ({
        rawFiles: state.CreateJobState.rawFiles || [],
        pipelineType: state.CreateJobState.pipelineType,
        archiveFiles: state.CreateJobState.archiveFiles || []
    }));
    const selectedState = useFileExplorerStore((state) => state.selectedState);
    const setRightPanelTab = useJobSetupStore((state) => state.setRightPanelTab);
    const hasFiles = rawFiles.length > 0;
    const previewLimit = 50;

    const isLibraryBased = pipelineType === JobPipelineTypeSchema.LibraryBased;
    const isLibraryGeneration = pipelineType === JobPipelineTypeSchema.LibraryGeneration;
    const hasArchiveFiles = archiveFiles.length > 0;
    
    const isRequired = isLibraryBased || 
                      (isLibraryGeneration && !hasArchiveFiles) ||
                      (!isLibraryBased && !isLibraryGeneration);

    const showRedBorder = !hasFiles && isRequired;

    const totalSize = rawFiles.reduce((acc, file) => acc + (file.sizeInBytes || 0), 0) / (1024 * 1024 * 1024);
    const selectedFolders = Object.entries(selectedState)
        .filter(([, meta]) => meta.isFolder)
        .map(([path]) => path);
    const hasSelectedFolders = selectedFolders.length > 0;
    const previewEntries = hasSelectedFolders
        ? selectedFolders.slice(0, previewLimit)
        : rawFiles.slice(0, previewLimit).map((file) => file.path);
    const hiddenEntriesCount = hasSelectedFolders
        ? Math.max(0, selectedFolders.length - previewEntries.length)
        : Math.max(0, rawFiles.length - previewEntries.length);

    return (
        <fieldset className="k-form-fieldset">
            <legend className="k-form-legend">
                <strong>Raw Files</strong> <SvgIcon style={{ paddingBottom: '5px' }} icon={svgIcons.fileDataIcon} />
            </legend>

            <div style={{ marginBottom: '10px' }}>
                <div
                    style={{
                        border: showRedBorder ? '1px solid #b81a1aff' : '1px solid #ccc',
                        borderRadius: '3px',
                        padding: '8px',
                        minHeight: '32px',
                        backgroundColor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4px',
                        cursor: hasFiles ? 'default' : 'pointer',
                    }}
                    onClick={hasFiles ? undefined : () => setRightPanelTab(0)}
                    title={hasFiles ? undefined : 'Click to switch to the Raw Files tab'}
                >
                    {hasFiles ? (
                        <>
                            <div style={{ width: '100%', fontWeight: 'bold', marginBottom: '4px', fontSize: '12px', color: '#555' }}>
                                {hasSelectedFolders
                                    ? `${selectedFolders.length} folders selected (${rawFiles.length} files, ${totalSize.toFixed(2)} GiB)`
                                    : `${rawFiles.length} files selected (${totalSize.toFixed(2)} GiB)`}
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto', width: '100%' }}>
                                {previewEntries.map((entry, idx) => (
                                    <span key={idx} style={{
                                        display: 'inline-block',
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #2196f3',
                                        borderRadius: '3px',
                                        padding: '2px 8px',
                                        fontSize: '12px',
                                        color: '#1976d2',
                                        margin: '2px',
                                    }}>
                                        {hasSelectedFolders ? entry : entry.split('/').pop()}
                                    </span>
                                ))}
                                {hiddenEntriesCount > 0 && (
                                    <span style={{
                                        display: 'inline-block',
                                        backgroundColor: '#f3f4f6',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '3px',
                                        padding: '2px 8px',
                                        fontSize: '12px',
                                        color: '#4b5563',
                                        margin: '2px',
                                    }}>
                                        +{hiddenEntriesCount} more
                                    </span>
                                )}
                            </div>
                        </>
                    ) : (
                        <span style={{ fontStyle: 'italic', fontSize: '13px' }}>
                            Select <strong>Raw Files</strong> in the tab on the right.
                        </span>
                    )}
                </div>
            </div>
        </fieldset>
    );
};
